import React, { useState, useEffect } from 'react';
import { XMarkIcon, PhoneIcon, ClockIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { LeadForCall, CallFormData } from '../types/Call';

interface CreateCallModalProps {
  leads: LeadForCall[];
  onClose: () => void;
  onCallCreated: (callData: any) => void;
  preSelectedLead?: LeadForCall;
  lockLeadSelection?: boolean;
  preSelectedCallType?: string;
}

const CreateCallModal: React.FC<CreateCallModalProps> = ({ 
  leads, 
  onClose, 
  onCallCreated, 
  preSelectedLead, 
  lockLeadSelection = false,
  preSelectedCallType
}) => {
  const [step, setStep] = useState(preSelectedLead || lockLeadSelection ? 2 : 1); // Skip lead selection if pre-selected or locked
  const [selectedLead, setSelectedLead] = useState<LeadForCall | null>(preSelectedLead || null);
  const [selectedPhone, setSelectedPhone] = useState<string>('');
  const [leadSearchTerm, setLeadSearchTerm] = useState<string>('');
  const [formData, setFormData] = useState<CallFormData>({
    leadId: 0,
    phoneNumber: '',
    callType: (preSelectedCallType as 'live' | 'recorded' | 'scheduled') || 'live',
    scheduledDate: '',
    scheduledTime: '',
    callDate: '',
    startTime: '',
    endTime: '',
    motiveAgenda: '',
    summary: '',
    duration: '',
    durationHours: 0,
    durationMinutes: 0,
    durationSeconds: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculate duration automatically
  const calculateDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return { hours: 0, minutes: 0, seconds: 0 };
    
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    
    if (end <= start) {
      // Handle case where end time is next day
      end.setDate(end.getDate() + 1);
    }
    
    const diffMs = end.getTime() - start.getTime();
    const totalSeconds = Math.floor(diffMs / 1000);
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return { hours, minutes, seconds };
  };

  // Filter leads based on search term
  const filteredLeads = leads.filter(lead => {
    const searchTerm = leadSearchTerm.toLowerCase();
    return (
      lead.firstName.toLowerCase().includes(searchTerm) ||
      lead.lastName.toLowerCase().includes(searchTerm) ||
      (lead.company && lead.company.toLowerCase().includes(searchTerm)) ||
      lead.email.toLowerCase().includes(searchTerm) ||
      lead.phoneNumbers.some(phone => phone.number.includes(searchTerm)) ||
      (lead.leadUid && lead.leadUid.toLowerCase().includes(searchTerm))
    );
  });

  // Reset form when modal opens
  useEffect(() => {
    if (preSelectedLead) {
      setStep(2);
      setSelectedLead(preSelectedLead);
      setFormData(prev => ({ ...prev, leadId: preSelectedLead.id }));
    } else {
      setStep(1);
      setSelectedLead(null);
    }
    setSelectedPhone('');
    setLeadSearchTerm('');
    setFormData(prev => ({
      ...prev,
      leadId: preSelectedLead?.id || 0,
      phoneNumber: '',
      callType: (preSelectedCallType as 'live' | 'recorded' | 'scheduled') || 'live',
      scheduledDate: '',
      scheduledTime: '',
      callDate: '',
      startTime: '',
      endTime: '',
      motiveAgenda: '',
      summary: '',
      duration: '',
      durationHours: 0,
      durationMinutes: 0,
      durationSeconds: 0
    }));
    setErrors({});
  }, [preSelectedLead, preSelectedCallType]);

  const handleLeadSelect = (lead: LeadForCall) => {
    setSelectedLead(lead);
    setFormData(prev => ({ ...prev, leadId: lead.id }));
    setStep(2);
  };

  const handlePhoneSelect = (phoneNumber: string) => {
    setSelectedPhone(phoneNumber);
    setFormData(prev => ({ ...prev, phoneNumber }));
    setStep(3);
  };

  const handleInputChange = (field: keyof CallFormData, value: string | number) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate duration for recorded calls
      if (field === 'startTime' || field === 'endTime') {
        const startTime = field === 'startTime' ? value as string : newData.startTime || '';
        const endTime = field === 'endTime' ? value as string : newData.endTime || '';
        
        if (startTime && endTime) {
          const duration = calculateDuration(startTime, endTime);
          newData.durationHours = duration.hours;
          newData.durationMinutes = duration.minutes;
          newData.durationSeconds = duration.seconds;
        }
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.motiveAgenda.trim()) {
      newErrors.motiveAgenda = 'Motive/Agenda is required';
    }

    if (formData.callType === 'scheduled') {
      if (!formData.scheduledDate) {
        newErrors.scheduledDate = 'Scheduled date is required';
      }
      if (!formData.scheduledTime) {
        newErrors.scheduledTime = 'Scheduled time is required';
      }
    }

    if (formData.callType === 'recorded') {
      if (!formData.callDate) {
        newErrors.callDate = 'Call date is required for recorded calls';
      }
      if (!formData.startTime) {
        newErrors.startTime = 'Start time is required for recorded calls';
      }
      if (!formData.endTime) {
        newErrors.endTime = 'End time is required for recorded calls';
      }
      if (!formData.summary || !formData.summary.trim()) {
        newErrors.summary = 'Summary is required for recorded calls';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      let callData: any = {
        leadId: formData.leadId,
        phoneNumber: formData.phoneNumber,
        callType: formData.callType,
        motiveAgenda: formData.motiveAgenda
      };

      // Add scheduled date for scheduled calls
      if (formData.callType === 'scheduled' && formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        callData.scheduledDate = scheduledDateTime.toISOString();
      }

      // Add fields for recorded calls
      if (formData.callType === 'recorded') {
        callData.summary = formData.summary;
        callData.callDate = formData.callDate;
        callData.startTime = formData.startTime;
        callData.endTime = formData.endTime;
        callData.durationHours = formData.durationHours;
        callData.durationMinutes = formData.durationMinutes;
        callData.durationSeconds = formData.durationSeconds;
      }

      onCallCreated(callData);
    } catch (error) {
      console.error('Error creating call:', error);
    }
  };

  const getCallTypeDescription = (callType: string) => {
    switch (callType) {
      case 'live':
        return 'Start a live call with timer and recording';
      case 'recorded':
        return 'Log a call that already happened';
      case 'scheduled':
        return 'Schedule a call for a future date and time';
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Create New Call</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium">Select Lead</span>
              </div>
              <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
              <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium">Select Phone</span>
              </div>
              <div className={`w-8 h-0.5 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
              <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  3
                </div>
                <span className="ml-2 text-sm font-medium">Call Details</span>
              </div>
            </div>
          </div>

          {/* Step 1: Select Lead */}
          {step === 1 && (
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900">
                {lockLeadSelection ? 'Selected Lead' : 'Select a Lead'}
              </h4>
              
              {lockLeadSelection && selectedLead ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedLead.firstName} {selectedLead.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedLead.company || selectedLead.email}
                      </div>
                      <div className="text-xs text-blue-600 mt-1">
                        Lead is pre-selected and locked
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {selectedLead.phoneNumbers.length} phone number{selectedLead.phoneNumbers.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search leads by name, company, email, or phone..."
                      value={leadSearchTerm}
                      onChange={(e) => setLeadSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {filteredLeads.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {leadSearchTerm ? 'No leads found matching your search.' : 'No leads available.'}
                      </div>
                    ) : (
                      filteredLeads.map((lead) => (
                      <div
                        key={lead.id}
                        onClick={() => handleLeadSelect(lead)}
                        className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {lead.firstName} {lead.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lead.company || lead.email}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {lead.phoneNumbers.length} phone number{lead.phoneNumbers.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Select Phone */}
          {step === 2 && selectedLead && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-900">Select Phone Number</h4>
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Back to Leads
                </button>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-900">
                  {selectedLead.firstName} {selectedLead.lastName}
                </div>
                <div className="text-sm text-gray-500">
                  {selectedLead.company || selectedLead.email}
                </div>
              </div>

              <div className="space-y-2">
                {selectedLead.phoneNumbers.map((phone, index) => (
                  <div
                    key={index}
                    onClick={() => handlePhoneSelect(phone.number)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <PhoneIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {phone.number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {phone.type}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Call Details */}
          {step === 3 && selectedLead && selectedPhone && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-900">Call Details</h4>
                <button
                  onClick={() => setStep(2)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Back to Phone
                </button>
              </div>

              {/* Lead Summary */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-900">
                  {selectedLead.firstName} {selectedLead.lastName}
                </div>
                <div className="text-sm text-gray-500">
                  {selectedPhone} • {selectedLead.company || selectedLead.email}
                </div>
              </div>

              {/* Call Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Call Type</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { value: 'live', label: 'Call Now', icon: PhoneIcon, description: 'Start a live call' },
                    { value: 'recorded', label: 'Record Previous Call', icon: ClockIcon, description: 'Log a past call' },
                    { value: 'scheduled', label: 'Schedule Call', icon: CalendarIcon, description: 'Set for future' }
                  ].map(({ value, label, icon: Icon, description }) => (
                    <div
                      key={value}
                      onClick={() => handleInputChange('callType', value)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.callType === value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className={`w-5 h-5 mr-3 ${formData.callType === value ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <div className={`text-sm font-medium ${formData.callType === value ? 'text-blue-900' : 'text-gray-900'}`}>
                            {label}
                          </div>
                          <div className={`text-xs ${formData.callType === value ? 'text-blue-700' : 'text-gray-500'}`}>
                            {description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call Type Description */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  {getCallTypeDescription(formData.callType)}
                </p>
              </div>

              {/* Scheduled Date/Time for Scheduled Calls */}
              {formData.callType === 'scheduled' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={formData.scheduledDate}
                      onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.scheduledDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.scheduledDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.scheduledDate}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      value={formData.scheduledTime}
                      onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.scheduledTime ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.scheduledTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.scheduledTime}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Call Date for Recorded Calls */}
              {formData.callType === 'recorded' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.callDate}
                    onChange={(e) => handleInputChange('callDate', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.callDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.callDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.callDate}</p>
                  )}
                </div>
              )}

              {/* Start and End Time for Recorded Calls */}
              {formData.callType === 'recorded' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.startTime ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.startTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.startTime}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.endTime ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.endTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.endTime}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Auto-calculated Duration Display for Recorded Calls */}
              {formData.callType === 'recorded' && formData.startTime && formData.endTime && (
                <div className="bg-blue-50 p-3 rounded-md">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Duration</label>
                  <div className="text-sm text-gray-600">
                    {(formData.durationHours || 0) > 0 && `${formData.durationHours || 0}h `}
                    {(formData.durationMinutes || 0) > 0 && `${formData.durationMinutes || 0}m `}
                    {(formData.durationSeconds || 0) > 0 && `${formData.durationSeconds || 0}s`}
                    {(formData.durationHours || 0) === 0 && (formData.durationMinutes || 0) === 0 && (formData.durationSeconds || 0) === 0 && '0s'}
                  </div>
                </div>
              )}

              {/* Motive/Agenda (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motive/Agenda <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.motiveAgenda}
                  onChange={(e) => handleInputChange('motiveAgenda', e.target.value)}
                  rows={3}
                  placeholder="What is the purpose of this call?"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.motiveAgenda ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.motiveAgenda && (
                  <p className="mt-1 text-sm text-red-600">{errors.motiveAgenda}</p>
                )}
              </div>

              {/* Summary for Recorded Calls */}
              {formData.callType === 'recorded' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Summary <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.summary}
                    onChange={(e) => handleInputChange('summary', e.target.value)}
                    rows={4}
                    placeholder="What was discussed during the call?"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.summary ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.summary && (
                    <p className="mt-1 text-sm text-red-600">{errors.summary}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-8">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            {step === 3 && (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Create Call
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCallModal;
